import React, { useRef, useEffect } from 'react';
import { Typography } from '../../../interfaces/typography';
import { SessionDataResult } from '../../../interfaces/services';
import { TranscriptRow } from './TranscriptRow';
import { SEC_TO_MS } from '../../../cfg/const';
import { SessionDetailView } from '../types';
interface TranscriptBodyProps {
  data: SessionDataResult;  
  currentTimeInMs: number;
  isPlaying: boolean;
  updateSelectedTranscript?: (selectedIdx: number | undefined) => void;
  selectedTranscriptIdx?: number;
  view: SessionDetailView;
}

const NoTranscripts: React.FC = () => (
  <div className="flex-1 flex flex-col items-center justify-center h-full min-h-[300px]">
    <div className="flex flex-col items-center gap-4 p-8 text-center">
      <div className="w-16 h-16 text-gray-300">
        <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
          <path
            d="M8 12H16M8 16H12M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <Typography variant="h3" color="gray">
        No Transcript Available
      </Typography>
      <Typography variant="mediumParagraph" color="gray" className="max-w-md">
        There is no transcript data available for this session.
      </Typography>
    </div>
  </div>
);

// Helper function to safely get timestamp in milliseconds
const getTimeInMs = (time: number | null): number => {
  if (time === null) return 0;
  return time*SEC_TO_MS;
};

export const TranscriptBody: React.FC<TranscriptBodyProps> = ({
  data,
  currentTimeInMs,
  isPlaying,
  updateSelectedTranscript,
  selectedTranscriptIdx,
  view
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to active transcript when playing
  useEffect(() => {
    const matchingTranscriptIdx = data.turns.findIndex(turn => {
      const startTime = getTimeInMs(turn.start);
      const endTime = getTimeInMs(turn.end);
      return currentTimeInMs >= startTime && currentTimeInMs <= endTime;
    })
    if (matchingTranscriptIdx === -1) return
    const selectedElement = document.querySelector(`[data-transcript-idx="${matchingTranscriptIdx}"]`);
    if (selectedElement && containerRef.current) {
      // Check if the element is not visible in the viewport
      const container = containerRef.current;
      const elementRect = selectedElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
        const isVisible = (
          elementRect.top >= containerRect.top &&
          elementRect.bottom <= containerRect.bottom
        );
        
        if (!isVisible) {
          selectedElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
        });
      }
    }
  }, [currentTimeInMs]);

  if (!data.turns || data.turns.length === 0) {
    return <NoTranscripts />;
  }

  return (
    <div 
      ref={containerRef}
      className="flex-1 overflow-y-auto space-y-3 py-4 h-full"
    >
      {data.turns.map((turn, idx) => {
        if (turn.text.trim() === '') { return null; }
        
        // Convert start/end times to numbers
        const startTime = getTimeInMs(turn.start);
        const endTime = getTimeInMs(turn.end);
        
        const isActive = isPlaying && 
          currentTimeInMs >= startTime && 
          currentTimeInMs <= endTime;
        
        // Check if this turn has a comment
        const hasComment = data.comments?.some(comment => 
          comment.transcriptIdx === idx
        ) ?? false;

        return (
          <TranscriptRow
            view={view}
            key={`${startTime}-${idx}`}
            turn={turn}
            index={idx}
            isSelected={selectedTranscriptIdx === idx || isActive}
            hasComment={hasComment}
            onClick={() => updateSelectedTranscript?.(idx)}
            sessionId={data.session.session_id}
          />
        );
      })}
    </div>
  );
}; 